'use strict';
import React, { useEffect } from 'react';
import { SMAPIRemoteCommand } from './src/remoteCommand';
import Constants from './src/constants';
import hoistNonReactStatics from 'hoist-non-react-statics';
import SecLib from './src/SecLib';
import CustomerChannel from './src/CustomerChannel';

const { EnvironmentTypes, NavigationTypes } = Constants;
const { COMPLETE_TRANSITION, NAVIGATE, BACK, JUMP_TO, RESET, POP, PUSH, POP_TO_TOP, REPLACE } = NavigationTypes;

function getPageId(props) {
	//react navigation v5 & v6
	if (props.route) {
		return props.route.name;
	}
	// react navigation  v3 & v2 & v1 || v4
	const state = props.navigation.state;
	return state.params?.name ?? state.routeName;
}

function addNavigationListener(props, action) {
	return cbOnAction => {
		const listener = props.navigation.addListener(SecLib._getListenerName(props, action), cbOnAction);
		return {
			remove: () => (props.route ? listener() : listener.remove()),
		};
	};
}

function withPageTracker(WrappedComponent) {
	const PageTrackerComponent = props => {
		const pageId = getPageId(props);
		const startLoadTime = Date.now();
		const focusListener = addNavigationListener(props, 'Focus');
		const blurListener = addNavigationListener(props, 'Blur');
		useEffect(() => {
			const endLoadTime = Date.now();
			let startFocusTime = 0;
			SecLib.checkForeground(pageId);
			SecLib._logLoadTime(pageId, (endLoadTime - startLoadTime) / 1000);
			const focus = focusListener(() => (startFocusTime = Date.now()));
			const blur = blurListener(() => SecLib._logStayTime(pageId, startFocusTime));

			return () => {
				focus.remove();
				blur.remove();
				// Only for navigation v4 or less, because blur listener not called
				!props.route && SecLib._logStayTime(pageId, startFocusTime);
				SecLib._logUnloaded(pageId);
				SecLib.checkBackground();
			};
		}, []);
		return <WrappedComponent {...props} />;
	};
	const ResultComponent = React.forwardRef((props, ref) => (
		<PageTrackerComponent {...props} _trackerForwardedRef={ref} />
	));

	hoistNonReactStatics(ResultComponent, WrappedComponent);
	return ResultComponent;
}

function withTracker(WrappedComponent, componentId) {
	const PageTrackerComponent = props => {
		useEffect(() => {
			SecLib.subComponents.push(componentId);
		}, []);
		return <WrappedComponent {...props} />;
	};
	const ResultComponent = React.forwardRef((props, ref) => (
		<PageTrackerComponent {...props} _trackerForwardedRef={ref} />
	));

	hoistNonReactStatics(ResultComponent, WrappedComponent);
	return ResultComponent;
}

function handleNavigationStateChange_v4(_, currentState, action) {
	const navigationType = SecLib._getNavigationType(action);
	const currentStateInfo = SecLib._getActiveRouteInfo(currentState);
	if (!currentStateInfo || currentStateInfo.routeName === SecLib.navState.currentRouteName) {
		return;
	}

	switch (navigationType) {
		case COMPLETE_TRANSITION:
			return;
		case NAVIGATE:
		case PUSH:
		case POP: //Enters only in case of a stack navigator going back using pop method
		case BACK: //Enters only in case of a stack navigator going back using goBack method
		case POP_TO_TOP:
		case JUMP_TO: //Enters only in case of a tab navigator
		case RESET: //Enters only in case of a stack navigator
		case REPLACE:
			SecLib.setPageName(currentStateInfo.routeName);
			SecLib._logLoaded(currentStateInfo.routeName);
			SecLib.logSubComponents();

			return;
	}
}

function handleNavigationStateChange_v5(navigationRef) {
	if (!navigationRef) {
		return;
	}
	const routeName = navigationRef.hasOwnProperty('current')
		? navigationRef.current.getCurrentRoute().name
		: navigationRef.getCurrentRoute().name;

	if (SecLib.navState.prevRoutName !== routeName) {
		SecLib.setPageName(routeName);
		SecLib._logLoaded(routeName);
		SecLib.navState.prevRoutName = routeName;
		SecLib.logSubComponents();
	}
}

function handleOnNavigationReady(navigationRef) {
	handleNavigationStateChange(navigationRef);
}

function handleNavigationStateChange(navigationRefOrPreviousState, currentState, action) {
	if (currentState && action) {
		handleNavigationStateChange_v4(navigationRefOrPreviousState, currentState, action);
	} else {
		handleNavigationStateChange_v5(navigationRefOrPreviousState);
	}
}

export default SecLib;
export {
	CustomerChannel,
	EnvironmentTypes,
	withTracker,
	withPageTracker,
	handleNavigationStateChange,
	SMAPIRemoteCommand,
	handleOnNavigationReady,
};
